import React from "react";
import styles from "./FieldStyler.module.css";
import toolbarStyles from "../../CygnusFormBuilderToolbar.module.css";
import { Form } from "react-bootstrap";
import { SectionStylerProps } from "./FieldStyler.utils";

/**
 * Resolves any CSS color value (hex, var(), color-mix()) to a 6-digit hex string
 * so it can be used as the value of <input type="color">.
 */
const resolveToHex = (cssValue: string | undefined, fallback: string): string => {
  if (!cssValue) return fallback;
  if (/^#[0-9a-fA-F]{6}$/i.test(cssValue)) return cssValue;

  const varMatch = cssValue.match(/^var\((--[\w_-]+)\)$/);
  if (varMatch) {
    const resolved = getComputedStyle(document.documentElement)
      .getPropertyValue(varMatch[1])
      .trim();
    if (/^#[0-9a-fA-F]{6}$/i.test(resolved)) return resolved;
  }

  // color-mix(in srgb, var(--xxx) N%, white) — resolve the var then apply tint in JS
  const colorMixMatch = cssValue.match(
    /color-mix\(\s*in\s+srgb\s*,\s*var\((--[\w_-]+)\)\s+(\d+(?:\.\d+)?)%\s*,\s*white\s*\)/i
  );
  if (colorMixMatch) {
    const varName = colorMixMatch[1];
    const pct = parseFloat(colorMixMatch[2]) / 100;
    const base = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (/^#[0-9a-fA-F]{6}$/i.test(base)) {
      const r = Math.round(parseInt(base.slice(1, 3), 16) * pct + 255 * (1 - pct));
      const g = Math.round(parseInt(base.slice(3, 5), 16) * pct + 255 * (1 - pct));
      const b = Math.round(parseInt(base.slice(5, 7), 16) * pct + 255 * (1 - pct));
      return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
    }
  }

  return fallback;
};

/**
 * SectionStyler manages the visual styling options for a form section,
 * including name, background, border, and title color.
 */
const SectionStyler: React.FC<SectionStylerProps> = ({
  selectedField,
  update,
  sectionVariant,
}) => {
  const { sectionName = "", sectionStyles = {} } = selectedField;
  const isFlat = sectionVariant === "flat";

  const isTransparent =
    !sectionStyles.backgroundColor ||
    sectionStyles.backgroundColor === "transparent";

  const handleTransparentToggle = () => {
    if (isTransparent) {
      update({ sectionStyles: { ...sectionStyles, backgroundColor: "#f8f8f8", borderColor: "#ddd" } });
    } else {
      update({ sectionStyles: { ...sectionStyles, backgroundColor: "transparent", borderColor: "transparent" } });
    }
  };

  return (
    <>
      <div className={toolbarStyles.formGroup}>
        <label>Section Name</label>
        <Form.Control
          type="text"
          value={sectionName}
          onChange={(e) => update({ sectionName: e.target.value })}
          placeholder="Enter section name"
          className="custom-input"
        />
      </div>

      <div className={styles.inlineColorGroup}>
        {/* Transparent toggle */}
        <div className={toolbarStyles.formGroup}>
          <label>Transparent</label>
          <button
            type="button"
            className={`${styles.transparentToggle} ${isFlat || isTransparent ? styles.transparentActive : ""}`}
            onClick={handleTransparentToggle}
            disabled={isFlat}
            title="Transparent background and border"
          />
        </div>

        {/* BG */}
        <div className={toolbarStyles.formGroup}>
          <label>BG</label>
          <input
            type="color"
            value={isFlat || isTransparent ? "#ffffff" : resolveToHex(sectionStyles.backgroundColor, "#f8f8f8")}
            onChange={(e) =>
              update({ sectionStyles: { ...sectionStyles, backgroundColor: e.target.value } })
            }
            className={`${styles.colorPicker} ${isFlat || isTransparent ? styles.colorPickerDisabled : ""}`}
            disabled={isFlat || isTransparent}
            title={isFlat ? "Controlled by Flat variant" : "Background color"}
          />
        </div>

        {/* Border */}
        <div className={toolbarStyles.formGroup}>
          <label>Border</label>
          <input
            type="color"
            value={isFlat || isTransparent ? "#ffffff" : resolveToHex(sectionStyles.borderColor, "#dddddd")}
            onChange={(e) =>
              update({ sectionStyles: { ...sectionStyles, borderColor: e.target.value } })
            }
            className={`${styles.colorPicker} ${isFlat || isTransparent ? styles.colorPickerDisabled : ""}`}
            disabled={isFlat || isTransparent}
            title={isFlat ? "Controlled by Flat variant" : "Border color"}
          />
        </div>

        {/* Title */}
        <div className={toolbarStyles.formGroup}>
          <label>Title</label>
          <input
            type="color"
            value={isFlat ? "#ffffff" : resolveToHex(sectionStyles.titleColor, "#333333")}
            onChange={(e) =>
              update({ sectionStyles: { ...sectionStyles, titleColor: e.target.value } })
            }
            className={`${styles.colorPicker} ${isFlat ? styles.colorPickerDisabled : ""}`}
            disabled={isFlat}
            title={isFlat ? "Controlled by Flat variant" : "Title color"}
          />
        </div>
      </div>

      {isFlat && (
        <p className={styles.flatNote}>Colors are fixed in Flat variant.</p>
      )}
    </>
  );
};

export default SectionStyler;
